// src/pages/ListingDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../utils/analytics';

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [qty, setQty] = useState(10);
  const [showInterest, setShowInterest] = useState(false);
  const [interestSubmitted, setInterestSubmitted] = useState(false);
  const [interestLoading, setInterestLoading] = useState(false);
  const [interestForm, setInterestForm] = useState({ name: user?.name || '', email: '', organisation: '', buyer_type: 'Corporate', volume_interest: '', notes: '' });

  useEffect(() => {
    api.get(`/listings/${id}`).then(r => { setListing(r.data); setQty(Math.min(10, r.data.qty_available)); }).catch(() => navigate('/listings'));
  }, [id]);

  if (!listing) return <div className="loading"><div className="spinner" /></div>;

  const color = { individual: 'var(--neon-green)', organization: 'var(--neon-cyan)', compliance_entity: 'var(--neon-orange)' }[listing.seller_type] || 'var(--neon-green)';
  const total = qty * listing.price_per_unit;
  const fee = Math.round(total * 0.003);
  const totalPayable = total + fee;
  const isOwnListing = Boolean(user?.id && listing?.seller_id && user.id === listing.seller_id);
  const hasFunds = typeof user?.balance === 'number' ? user.balance >= totalPayable : false;

  const handleBuy = async () => {
    navigate('/coming-soon?feature=buy_credits');
  };

  const handleInterestSubmit = async (e) => {
    e.preventDefault();
    if (!interestForm.name || !interestForm.email || !interestForm.organisation) {
      toast.error('Please fill in name, email, and organisation');
      return;
    }
    setInterestLoading(true);
    try {
      await api.post(`/listings/${id}/interest`, interestForm);
      track('interest_submitted', { listing_id: id, buyer_type: interestForm.buyer_type });
      setInterestSubmitted(true);
      toast.success('Interest registered. The project developer will be in touch.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit interest');
    } finally {
      setInterestLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '3rem 2.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <span onClick={() => navigate('/listings')} style={{ cursor: 'pointer', color: 'var(--muted)', fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', letterSpacing: '2px' }}>← BACK TO MARKETPLACE</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
        <div>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', letterSpacing: '3px', color, textTransform: 'uppercase', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}` }} />
            {listing.seller_type?.replace('_', ' ').toUpperCase()}
          </div>
          <h1 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '2.5rem', letterSpacing: '3px', marginBottom: '0.5rem' }}>{listing.title}</h1>
          <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>{listing.project}</p>
          {listing.trust_level && (
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: listing.trust_level === 'verified' ? 'var(--neon-green)' : listing.trust_level === 'docs_checked' ? 'var(--neon-cyan)' : 'var(--neon-yellow)' }}>
                {listing.trust_level === 'verified' ? '✓ Verified by admin' : listing.trust_level === 'docs_checked' ? '✓ Documents checked by admin' : '○ Self-reported — verify independently'}
              </span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem', marginBottom: '2rem' }}>
            {[
              { l: 'Available', v: `${listing.qty_available.toLocaleString()} tCO₂e`, c: color },
              { l: 'Vintage', v: listing.vintage, c: 'var(--text)' },
              { l: 'Price', v: `₹${listing.price_per_unit.toLocaleString()}`, c: color },
              { l: 'Verified By', v: listing.verified_by, c: 'var(--text)' },
              { l: 'Verification ID', v: listing.verification_id, c: 'var(--muted2)' },
              { l: 'Methodology', v: listing.methodology, c: 'var(--muted2)' },
              ...(listing.project_category ? [{ l: 'Category', v: listing.project_category, c: 'var(--muted2)' }] : []),
              ...(listing.project_status ? [{ l: 'Project Status', v: listing.project_status.replace('_', ' '), c: 'var(--neon-cyan)' }] : []),
              ...(listing.registry ? [{ l: 'Registry', v: listing.registry, c: 'var(--muted2)' }] : []),
              ...(listing.estimated_annual_co2e ? [{ l: 'Est. Annual CO₂e', v: `${Number(listing.estimated_annual_co2e).toLocaleString()} tCO₂e`, c: 'var(--neon-green)' }] : []),
            ].map(({ l, v, c }) => (
              <div key={l} className="card">
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.55rem', letterSpacing: '2px', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{l}</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: c }}>{v}</div>
              </div>
            ))}
          </div>

          {listing.description && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.2rem', letterSpacing: '2px', marginBottom: '0.8rem' }}>ABOUT THIS PROJECT</div>
              <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: '1.9' }}>{listing.description}</p>
            </div>
          )}

          {listing.sdg_goals?.length > 0 && (
            <div className="card">
              <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.2rem', letterSpacing: '2px', marginBottom: '1rem' }}>SDG ALIGNMENT</div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {listing.sdg_goals.map(g => <span key={g} className="badge badge-cyan">{g}</span>)}
              </div>
            </div>
          )}
        </div>

        {/* BUY PANEL */}
        <div>
          <div className="card" style={{ borderTop: `2px solid ${color}`, position: 'sticky', top: '6rem' }}>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.5rem', letterSpacing: '2px', marginBottom: '1.5rem' }}>PURCHASE CREDITS</div>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '2.5rem', color, letterSpacing: '2px', marginBottom: '0.3rem' }}>₹{listing.price_per_unit.toLocaleString()}</div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>per tCO₂e</div>

            <div className="form-group">
              <label className="form-label">Quantity (tCO₂e)</label>
              <input
                className="form-input"
                type="number"
                inputMode="numeric"
                step="1"
                min="1"
                max={listing.qty_available}
                value={qty}
                onChange={(e) => {
                  const next = Math.floor(Number(e.target.value) || 1);
                  setQty(Math.max(1, Math.min(listing.qty_available, next)));
                }}
              />
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'var(--muted)', marginTop: '0.3rem' }}>Max: {listing.qty_available.toLocaleString()}</div>
            </div>

            <div style={{ background: 'var(--surface2)', padding: '1rem', fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', marginBottom: '1rem' }}>
              {[['Subtotal', `₹${total.toLocaleString()}`], ['Platform Fee (0.3%)', `₹${fee.toLocaleString()}`], ['Total Payable', `₹${(total + fee).toLocaleString()}`]].map(([l, v], i) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: i < 2 ? '0.4rem' : 0, fontWeight: i === 2 ? 700 : 400 }}>
                  <span style={{ color: 'var(--muted)' }}>{l}</span><span style={{ color: i === 2 ? color : 'var(--text)' }}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{ background: 'var(--surface)', padding: '0.8rem', marginBottom: '1rem', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)' }}>Your INR Balance</span>
                <span style={{ color: user ? ((user.balance >= total + fee) ? 'var(--neon-green)' : 'var(--neon-pink)') : 'var(--muted)' }}>
                  {user ? `₹${user.balance.toLocaleString()}` : 'Login required'}
                </span>
              </div>
            </div>

            {listing.seller_info && (
              <div style={{ border: '1px solid var(--border)', padding: '0.8rem', marginBottom: '1rem', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem' }}>
                <div style={{ color: 'var(--muted)', marginBottom: '0.4rem' }}>SELLER INFO</div>
                <div>{listing.seller_info.name}</div>
                <div style={{ color: 'var(--muted)' }}>{listing.seller_info.gci_id}</div>
                <div style={{ color: 'var(--neon-green)', marginTop: '0.3rem' }}>✅ {listing.seller_info.kyc_status?.toUpperCase()}</div>
              </div>
            )}

            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '0.9rem' }}
              onClick={handleBuy}
              disabled={listing.qty_available === 0 || isOwnListing || (user && !hasFunds)}
            >
              {listing.qty_available === 0 ? 'SOLD OUT' : 'BUY NOW →'}
            </button>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'var(--muted)', textAlign: 'center', marginTop: '0.8rem', letterSpacing: '1px' }}>Settlement: T+2 via GCI Registry</div>
          </div>

          {/* Express Interest — deal-room discovery panel */}
          <div className="card" style={{ marginTop: '1.5rem', borderColor: 'rgba(0,229,255,0.2)' }}>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1rem', letterSpacing: '2px', marginBottom: '0.5rem', color: 'var(--neon-cyan)' }}>EXPRESS INTEREST</div>
            <p style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '1rem' }}>
              Not ready to buy? Signal your intent to the project developer for an offline conversation. No commitment or payment required.
            </p>

            {/* Disclaimer */}
            <div style={{ background: 'rgba(255,238,0,0.05)', border: '1px solid rgba(255,238,0,0.15)', padding: '0.7rem', marginBottom: '1rem', fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: '#ffe873', lineHeight: '1.5' }}>
              Information only. CarbonX is not a party to any transaction. Conduct your own due diligence before proceeding.
            </div>

            {interestSubmitted ? (
              <div style={{ background: 'rgba(0,255,136,0.07)', border: '1px solid rgba(0,255,136,0.2)', padding: '1rem', fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--neon-green)', lineHeight: '1.6' }}>
                ✅ Interest registered. The project developer has been notified and will reach out to you directly.
              </div>
            ) : showInterest ? (
              <form onSubmit={handleInterestSubmit}>
                <div className="form-group">
                  <label className="form-label">Full name</label>
                  <input className="form-input" type="text" value={interestForm.name} onChange={(e) => setInterestForm({ ...interestForm, name: e.target.value })} placeholder="Your name" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={interestForm.email} onChange={(e) => setInterestForm({ ...interestForm, email: e.target.value })} placeholder="you@organisation.com" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Organisation</label>
                  <input className="form-input" type="text" value={interestForm.organisation} onChange={(e) => setInterestForm({ ...interestForm, organisation: e.target.value })} placeholder="Company or NGO name" required />
                </div>
                <div className="form-group">
                  <label className="form-label">I am a</label>
                  <select className="form-select" value={interestForm.buyer_type} onChange={(e) => setInterestForm({ ...interestForm, buyer_type: e.target.value })}>
                    <option>Corporate</option>
                    <option>SME / Startup</option>
                    <option>Project Developer</option>
                    <option>Investor</option>
                    <option>Individual</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Approx. volume interest (tCO₂e/year) — optional</label>
                  <input className="form-input" type="text" value={interestForm.volume_interest} onChange={(e) => setInterestForm({ ...interestForm, volume_interest: e.target.value })} placeholder="e.g. 500" />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes / questions — optional</label>
                  <textarea className="form-textarea" rows={3} value={interestForm.notes} onChange={(e) => setInterestForm({ ...interestForm, notes: e.target.value })} placeholder="What would you like to discuss?" />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button type="submit" className="btn btn-primary" disabled={interestLoading} style={{ flex: 1, justifyContent: 'center' }}>
                    {interestLoading ? 'Submitting...' : 'Submit Interest'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowInterest(false)}>Cancel</button>
                </div>
              </form>
            ) : (
              <button type="button" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', borderColor: 'rgba(0,229,255,0.3)', color: 'var(--neon-cyan)' }} onClick={() => setShowInterest(true)}>
                I'm Interested →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
